import { useAuth0 } from "@auth0/auth0-react";
import { sum } from "lodash";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  BarChartOutlined,
  Construction,
  Delete,
  Edit,
  Output,
  Share,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { UploadSkeleton } from "./UploadSkeleton";
import {
  buildUpload,
  createNotification,
  deleteAudience,
  getInsight,
  requestTravelTime,
  saveAudience,
  shareAudience,
  updateAudienceName,
  updateAudienceStatus,
  upload,
  validateFile,
} from "@/api";
import {
  AudienceSegmentsSize,
  AudienceSize,
  ErrorPanel,
  FileUpload,
  HelpBox,
  HelpText,
  useActivePage,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { AudienceMiniStats, GeoMode, SelectableMenuCard, TravelTimeMode } from "@/components/molecules";
import { useAudienceContext, useDialogs, useSnackbar } from "@/context";
import { useAudience } from "@/hooks";
import {
  AudienceSegment,
  AudienceSummary,
  Breadcrumb,
  PageStatus,
  ValidFile,
} from "@/types";
import {
  checkAudienceSize,
  getAudienceBreadcrumb,
  uploadModeOptions,
} from "@/utils";

const UploadPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { state, dispatch } = useAudienceContext();
  const navigate = useNavigate();
  const { openDialog, closeDialog } = useDialogs();

  let { audienceId } = useParams<{ audienceId: string }>();
  audienceId = audienceId === "new" ? undefined : audienceId;
  const {
    audience,
    loading: audienceLoading,
    error: audienceError,
  } = useAudience(audienceId, "upload");
  const uploadFile = audience?.upload_file;
  const uploadMode = audience?.upload_mode;
  const audienceSharedToUser = audience?.shared;
  const { openSnackbar } = useSnackbar();

  const activePage = useActivePage();
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const pageLoading = audienceLoading;
  const processingStatus = audience && audience.status === 'processing';
  const errorStatus = audience && audience.status === 'error';
  const completeStatus = audience && audience.status === 'complete';
  const readOnly = audience?.shared || processingStatus;

  const [validFile, setValidFile] = useState<ValidFile | null>(null);
  const [checkingFile, setCheckingFile] = useState<boolean>(false);

  const [pageStatus, setPageStatus] = useState<PageStatus>({loading: false, error: false, type: '', message: ''});
  const [updateError, setUpdateError] = useState<string | null>(null);

  const readyToUpload = uploadMode && uploadFile && uploadFile instanceof File;
  const readyToBuild = readyToUpload && validFile?.format;
  const readyToProcess = readyToUpload && audience && !processingStatus;

  useEffect(() => {
    setBreadcrumbs(
      getAudienceBreadcrumb(activePage, audience, audienceLoading)
    );
  }, [activePage, audience, audienceLoading]);

  useEffect(() => {
    state.change && setPageStatus({loading: false, error: false, type: '', message: ''});
  }, [state.change]);

  useEffect(() => {
    // This error has likely come from Traveltime
    errorStatus && setPageStatus({loading: false, error: true, type: 'insight', message: 'Traveltime Error'})
  }, [errorStatus]);

  const extractSegments = (summary: AudienceSummary[]): AudienceSegment[] => {
    return summary.map((x, index) => ({
      segment_id: index + 1,
      segment_name: x.segment ?? `Segment ${index + 1}`,
    }));
  };

  const handleChangeMode = (mode: string) => {
    dispatch({ type: "CHANGE_UPLOAD_MODE", payload: mode });
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files) {
      dispatch({ type: "ADD_UPLOAD_FILE", payload: files[0] });
    }
  };

  useEffect(() => {
    async function checkFile(file: File, mode: string) {
      setValidFile(null);
      setCheckingFile(true);

      const token = await getAccessTokenSilently();
      const { data, error } = await validateFile(token, file, mode);

      if (data) {
        setValidFile(data);
      }
      if (error) {
        setValidFile({ format: false, segment: false, error: true });
      }

      setCheckingFile(false);
    }

    readyToUpload && checkFile(uploadFile, uploadMode);
  }, [getAccessTokenSilently, readyToUpload, uploadFile, uploadMode]);

  const handleBuildAudience = async () => {
    const token = await getAccessTokenSilently();

    if (readyToBuild) {
      setPageStatus({loading: true, error: false, type: 'build', message: 'Building Upload'})
      const { data, error } = await buildUpload(token, uploadFile, uploadMode, audienceId, audience.travel_time);

      if (data) {
        if (checkAudienceSize(data.audience_size[0])) {
          dispatch({ type: "BUILD_AUDIENCE", payload: data });
          if (data.audience_size.length > 1) {
            const audienceSegments = extractSegments(data.audience_size);
            dispatch({ type: "ADD_SEGMENTS", payload: audienceSegments });
          }
          setPageStatus({loading: false, error: false, type: 'build', message: ''})
        } else {
          setPageStatus({loading: false, error: true, type: 'build', message: "No valid geos were found in your file, please check the file contents and try again"})
        }
      }

      if (error) {
        setPageStatus({loading: false, error: true, type: 'build', message: error.message})
      }
    }
  };

  const handleAudienceInsight = () => {
    if (!audience) return;

    if (audience.audience_id && audience.audience_id !== "") {
      // Existing audience - if change show warning and rebuild otherwise go to insights
      if (state.change) {
        openDialog({
          type: "confirm",
          props: {
            message: `\nProceeding will mean that your saved audience will get overwritten.\nCreate a new audience if you don't want this to happen.\n`,
            onConfirm: async () => {
              await handleUpdateAudience(
                audience.audience_name,
                audience.project_name
              );
            },
          },
        });
      } else {
        navigate("insight");
      }
    } else {
      // New audience - show save audience dialog then go to insights
      openDialog({
        type: "createAudience",
        props: {
          onConfirm: handleUpdateAudience,
        },
      });
    }
  };

  const handleAudienceExport = () => {
    navigate("export");
  };

  const handleUpdateAudience = async (
    audienceName: string,
    projectName: string | null
  ) => {
    const token = await getAccessTokenSilently();
    const client = process.env.REACT_APP_CLIENT || 'core';
    closeDialog();

    if (audience && readyToProcess) {

      // Upload the file to create an audience
      // This will also trigger a travel_time request
      setPageStatus({loading: true, error: false, type: 'upload', message: 'Uploading Data'})
      const uploadData = await upload(token, uploadFile, uploadMode, audienceId, audience.travel_time);

      if (uploadData.data) {

        // Prepare the audience data to save
        let newAudienceName = audienceName ?? audience.audience_name;
        let newProjectName = projectName ?? audience.project_name;
        let newUploadFile = { name: uploadFile.name };
        let updatedAudience = {
          ...audience,
          audience_id: uploadData.data.audience_id,
          audience_name: newAudienceName,
          project_name: newProjectName,
          upload_file: newUploadFile,
        };

        // Save the audience - this builds the audience tables too
        const audienceData = await saveAudience(token, updatedAudience, true);

        if (audienceData.data) {
          let newAudience = audienceData.data.filter(
            (audience) =>
              audience.audience_name === updatedAudience.audience_name
          )[0];
          newAudience.status = "processing";
          dispatch({
            type: "UPDATE_AUDIENCE",
            payload: {
              audiences: audienceData.data,
              currentAudience: newAudience,
            },
          });

          if (audience.upload_mode === 'travel_time' && audience.travel_time) {

            setPageStatus({loading: true, error: false, type: 'upload', message: 'Requesting Traveltime Audience'})

            // Request traveltime
            const payload = {
              ...audience.travel_time,
              audience_id: newAudience.audience_id,
              audience_name: newAudience.audience_name,
              client
            }
            const traveltime = await requestTravelTime(token, payload)

            if (traveltime.data) {

              // Update the audience size and navigate to the audience page
              navigate(`../${traveltime.data.audience_id}`, {
                relative: "path",
              });
            }

            if (traveltime.error) {
              dispatch({ type: "RESET_AUDIENCE_STATUS" });
              dispatch({ type: "SET_STATUS", payload: "error" });
              setPageStatus({loading: false, error: true, type: 'insight', message: traveltime.error.message});

              // Delete audience
              await deleteAudience(
                token,
                newAudience.audience_name,
                newAudience.audience_id,
                newAudience.audience_type,
                client
              )
            }

          } else {

            setPageStatus({loading: true, error: false, type: 'insight', message: 'Calculating Audience Insight'})

            // Get the insight results
            const insight = await getInsight(
              token,
              newAudience.audience_id,
              true,
              newAudience.client
            );

            if (insight.data) {
              dispatch({ type: "CREATE_INSIGHT", payload: { ...insight.data } });
              setPageStatus({loading: false, error: false, type: 'insight', message: ''})

              const notification = {
                client: newAudience.client,
                message: `Upload "${newAudience.audience_name}" successfully created`,
                category: "Upload",
                data: { audience_id: newAudience.audience_id}
              }
              await createNotification(token, notification).then( () =>
                navigate(`../${newAudience.audience_id}/insight`, {
                  relative: "path",
                })
              );

              // Update the audience status in the database
              const status = await updateAudienceStatus(
                token,
                newAudience.audience_id,
                'complete'
              );

              if (status.error) {
                setPageStatus({loading: false, error: true, type: 'insight', message: status.error.message})
              }
            }

            if (insight.error) {
              dispatch({ type: "SET_STATUS", payload: "error" });
              dispatch({ type: "RESET_AUDIENCE_STATUS" });
              setPageStatus({loading: false, error: true, type: 'insight', message: insight.error.message})
            }
          }

        }

        if (audienceData.error) {
          dispatch({ type: "SET_STATUS", payload: "error" });
          setPageStatus({loading: false, error: true, type: 'insight', message: audienceData.error.message})
        }
      }

      if (uploadData.error) {
        setPageStatus({loading: false, error: true, type: 'insight', message: uploadData.error.message})
      }
    }
  };

  const handleUpdateAudienceName = async (
    audienceId: string,
    audienceName: string,
    projectName: string | null
  ) => {
    const token = await getAccessTokenSilently();

    const { data, error } = await updateAudienceName(
      token,
      audienceId,
      audienceName,
      projectName
    );

    if (data) {
      dispatch({
        type: "CHANGE_NAME",
        payload: { audienceId, audienceName, projectName },
      });
      closeDialog();
    }

    if (error) {
      closeDialog();
      setUpdateError(error.message);
    }
  };

  const handleShareAudience = async (ids: number[]) => {
    const accessToken = await getAccessTokenSilently();

    const { data, error } = await shareAudience(
      accessToken,
      audience.audience_id,
      ids
    );

    if (data) {
      openSnackbar(data.message, "success");
      dispatch({
        type: "SHARE_AUDIENCE",
        payload: { audienceId: audience.audience_id, userIds: ids },
      });
      closeDialog();
    }

    if (error) {
      openSnackbar(error.message, "error");
      setUpdateError(error.message);
      closeDialog();
    }
  };

  const summariseAudience = (summary: AudienceSummary[]) => {
    return {
      num_postcodes: sum(summary.map((x) => x.num_postcodes)),
      num_sectors: sum(summary.map((x) => x.num_sectors)),
      num_households: sum(summary.map((x) => x.num_households)),
      population_count: sum(summary.map((x) => x.population_count)),
    };
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="contained"
        size="small"
        color="error"
        disableElevation
        startIcon={<Delete />}
        disabled={
          pageLoading ||
          pageStatus.loading ||
          audienceId === undefined ||
          audienceSharedToUser ||
          processingStatus ||
          errorStatus
        }
        onClick={() =>
          openDialog({
            type: "deleteAudience",
            props: {
              audience,
              redirect: "/upload",
            },
          })
        }
      >
        Delete
      </Button>
      {!audienceSharedToUser && (
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<Share />}
          disabled={
            pageLoading ||
            pageStatus.loading ||
            audienceId === undefined||
            processingStatus ||
            errorStatus
          }
          onClick={() =>
            openDialog({
              type: "shareAudience",
              props: {
                selectedEntity: audience,
                onShare: async (ids: number[]) => {
                  await handleShareAudience(ids);
                },
              },
            })
          }
        >
          Share
        </Button>
      )}
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Construction />}
        loading={pageStatus.loading && pageStatus.type === 'build'}
        disabled={
          state.built ||
          !readyToBuild ||
          !validFile?.format ||
          audienceSharedToUser||
          processingStatus
        }
        onClick={handleBuildAudience}
      >
        Build
      </Button>
      {audience && audience.audience_size && (
        <>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<BarChartOutlined />}
            loading={(pageStatus.loading && pageStatus.type === 'insight') || processingStatus}
            disabled={!state.built || errorStatus}
            onClick={handleAudienceInsight}
          >
            Insight
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<Output />}
            disabled={!state.built || pageStatus.loading || processingStatus || errorStatus || !completeStatus}
            onClick={handleAudienceExport}
          >
            Export
          </Button>
        </>
      )}
    </Stack>
  );

  return (
    <PageContainer
      breadcrumbs={audienceError ? undefined : breadcrumbs}
      actions={audienceError ? undefined : actionButtons}
      loading={pageStatus.loading}
      loadingText={pageStatus.message}
    >
      {pageLoading ? (
        <UploadSkeleton />
      ) : (
        <Grid container spacing={2} sx={{ pb: 4 }}>
          <Grid container size={12}>
            <ErrorPanel
              audienceError={audienceError}
              audienceUpdateError={updateError}
              buildError={(pageStatus.error && pageStatus.type === 'build') ? pageStatus.message : null}
              insightError={(pageStatus.error && pageStatus.type === 'insight') ? pageStatus.message : null}
            />
          </Grid>
          {audience && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audienceId && !audienceSharedToUser && (
                    <HelpBox title="Edit Audience/Project Name">
                      <IconButton
                        size="small"
                        onClick={() =>
                          openDialog({
                            type: "editAudience",
                            props: {
                              audienceId: audienceId,
                              audienceName: audience.audience_name,
                              projectName: audience.project_name,
                              onConfirm: async (
                                audienceId: string,
                                audienceName: string,
                                projectName: string | null
                              ) => {
                                await handleUpdateAudienceName(
                                  audienceId,
                                  audienceName,
                                  projectName
                                );
                              },
                            },
                          })
                        }
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </HelpBox>
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats
                    audienceSize={summariseAudience(audience.audience_size)}
                  />
                )}
              </Grid>

              {audience?.shared && (
                <Grid size={12}>
                  <Alert variant="filled" severity="info">
                    You are viewing an upload that has been shared with you and
                    therefore any edit functionality has been disabled. You may
                    still view the insights for and export this upload. We will
                    soon be adding the ability to clone this upload so that you
                    can make changes.
                  </Alert>
                </Grid>
              )}
              {processingStatus && (
                <Grid size={12}>
                  <Alert variant="filled" severity="info">
                    This audience is still processing, please wait. You will receive
                    a notification when the insights are complete.
                  </Alert>
                </Grid>
              )}

              {/* Heading and Mode Select */}

              <Grid size={12}>
                <HelpText
                  text="Select Mode"
                  helpText="Define the type of upload audience you are creating"
                  variant="body1"
                />
              </Grid>
              <Grid container size={12} spacing={2}>
                {uploadModeOptions.map((option, index) => (
                  <Grid key={index} pb={2}>
                    <SelectableMenuCard
                      title={option.label}
                      text={option.description}
                      onClick={() => handleChangeMode(option.value)}
                      icon={option.icon}
                      disabled={option.disabled || readOnly}
                      flag={option.flag}
                      flagDesc={option.flagDescription}
                      selected={audience.upload_mode === option.value}
                      height="152px"
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Mode Options */}
              {audience.upload_mode && (
                <>
                  {audience.upload_mode === 'travel_time' ? (
                      <TravelTimeMode audience={audience} readOnly={readOnly} />
                    )
                  : (
                      <GeoMode readOnly={readOnly} />
                    )
                  }
                  {state.change && !readyToUpload && uploadFile && (
                    <Typography variant="caption" color="error">
                      Please re-upload your file if you would like to re-build your Audience,
                      the filename is listed below for convenience.
                    </Typography>
                  )}
                  <Grid size={12}>
                    <FileUpload
                      currentFile={audience.upload_file}
                      validFile={validFile}
                      loading={checkingFile}
                      onChange={handleFileUpload}
                      allowSegments={true}
                      disabled={readOnly}
                    />
                  </Grid>
                </>
              )}

              {/* Audience Size */}
              {audience.audience_size && (
                <>
                  <Grid size={12} mt={2}>
                    <HelpText
                      text="Audience Size"
                      helpText="The number of postcodes in your upload, and how many households/people this represents"
                      variant="body1"
                    />
                  </Grid>
                  {audience.audience_size.length > 1 ? (
                    <AudienceSegmentsSize data={audience.audience_size} />
                  ) : (
                    <AudienceSize data={audience.audience_size[0]} />
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default UploadPage;
